import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PRIVATE_PATH } from '../../../constants';
import { useGetOnDemandStaffByManagementCompanyQuery } from '../../../redux/managementCompanyWorkforce';
import { useGetOnDemandStaffProfessionalCredentialsQuery } from '../../../redux/onDemandStaffProfessionalCredentials';
import SidebarWrapper from '../../../sections/SidebarWrapper';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import CredentialStatusPill from '../../../components/CredentialStatusPill/CredentialStatusPill';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import OnDemandStaffCredentialData from './OnDemandStaffCredentialData';

// Reuse the existing credentials-page styling so this ShiftMed page matches the workforce credentials design.
import '../../flex/professionals/credentials/FlexProfessionalCredentials.scss';
import './OnDemandStaffProfessionalDetails.scss';

// Read-only credentials page for a ShiftMed on-demand professional. Rows are the facility-packet credential
// requirements applicable to the professional; the Type(s) pills are the required subtypes they satisfy with a
// verified, unexpired credential. The professional's name is passed via router state from the ShiftMed list;
// on a direct URL / refresh (no state) the title falls back to a plain "Credentials".
const OnDemandStaffProfessionalDetails = () => {
	const history = useHistory();
	const location = useLocation();
	// The originating MC travels in the route itself (rather than redux, which resets on a hard refresh) since
	// the credentials endpoint is MC-scoped: guessing a different, merely-authorized MC would silently return
	// that MC's facility-packet requirements instead of the ones this professional was opened under.
	const { managementCompanyId, caregiverId } = useParams();

	const [expandedRows, setExpandedRows] = useState(null);

	const { data: credentials, isFetching } = useGetOnDemandStaffProfessionalCredentialsQuery(
		{ caregiverId, managementCompanyId },
		{ skip: !managementCompanyId, refetchOnMountOrArgChange: true }
	);

	// Only credentials the professional actually holds (a Type(s) pill) are expandable — an empty row has no
	// instance detail to show.
	const allowExpansion = useCallback((row) => row.types.length > 0, []);

	const rowExpansionTemplate = useCallback(
		(row) => (
			<OnDemandStaffCredentialData
				caregiverId={caregiverId}
				managementCompanyId={managementCompanyId}
				credentialType={row.credentialType}
			/>
		),
		[caregiverId, managementCompanyId]
	);

	// The expander column only reacts to clicks on the chevron itself; clicking anywhere else on the row must
	// also toggle it. PrimeReact skips onRowClick for clicks on the chevron button (or any other clickable
	// element) already, so this never double-toggles.
	const toggleRowExpansion = useCallback(
		(row) => {
			if (!allowExpansion(row)) {
				return;
			}
			setExpandedRows((previousExpandedRows) => {
				const nextExpandedRows = { ...(previousExpandedRows || {}) };
				if (nextExpandedRows[row.credentialType]) {
					delete nextExpandedRows[row.credentialType];
				} else {
					nextExpandedRows[row.credentialType] = true;
				}
				return nextExpandedRows;
			});
		},
		[allowExpansion]
	);

	// On a direct URL / refresh, router state is gone, so resolve the professional's name from the ShiftMed
	// workforce list (same MC, already used/cached by the tab) instead of leaving the title without a name.
	const { data: shiftMedStaff } = useGetOnDemandStaffByManagementCompanyQuery(managementCompanyId, {
		skip: !managementCompanyId
	});

	const passedName = location.state?.professionalName;
	const resolvedName = useMemo(() => {
		if (passedName) {
			return passedName;
		}
		return shiftMedStaff?.find((professional) => String(professional.caregiverId) === String(caregiverId))?.name;
	}, [passedName, shiftMedStaff, caregiverId]);
	const professionalName = resolvedName && resolvedName !== '-' ? resolvedName : '';
	const credentialsPath = `${PRIVATE_PATH.WORKFORCE_PAGE}/${managementCompanyId}/on-demand-staff/${caregiverId}/credentials`;

	const sidebarItems = [
		{
			label: 'Credentials',
			editTitle: professionalName ? `Credentials - ${professionalName}` : 'Credentials',
			isEditing: true,
			path: credentialsPath,
			className: 'flex-credentials-title'
		}
	];

	const handleBackToWorkforce = () => history.push(PRIVATE_PATH.WORKFORCE_PAGE);

	const typesTemplate = useCallback(
		(row) => (
			<div className="status-container">
				{row.types.map((type) => (
					<CredentialStatusPill key={type.code} status="VERIFIED" title={type.name} />
				))}
			</div>
		),
		[]
	);

	return (
		<SidebarWrapper
			sidebarTitle="Professional"
			sidebarItems={sidebarItems}
			backTitle="Back to Workforce"
			backAction={handleBackToWorkforce}
			className="flex-professionals-sidebar on-demand-staff-professional"
		>
			<WithOnlyOnDesktop>
				<div className="flex-professionals-credentials on-demand-staff-credentials">
					<p className="credentials-info">
						Only <strong>non-expired</strong>, <strong>verified</strong> credentials will show for ShiftMed
						professionals
					</p>
					<DataTable
						value={credentials || []}
						tableStyle={{ tableLayout: 'fixed' }}
						size="small"
						stripedRows
						dataKey="credentialType"
						loading={isFetching}
						loadingIcon={<LoadingSpinner />}
						emptyMessage="No results found"
						expandedRows={expandedRows}
						onRowToggle={(event) => setExpandedRows(event.data)}
						onRowClick={(event) => toggleRowExpansion(event.data)}
						rowExpansionTemplate={rowExpansionTemplate}
						rowClassName={(row) => (allowExpansion(row) ? 'expandable-row' : '')}
					>
						<Column expander={allowExpansion} bodyClassName="expandable" style={{ width: '5%' }} />
						<Column field="name" header="Credential" style={{ width: '35%' }} sortable />
						<Column header="Type(s)" style={{ width: '60%' }} body={typesTemplate} />
					</DataTable>
				</div>
			</WithOnlyOnDesktop>
		</SidebarWrapper>
	);
};

export default OnDemandStaffProfessionalDetails;
